export type SubagentOpenTarget =
  | { kind: 'file'; path: string }
  | { kind: 'endpoint'; path: string };

const FILE_TOOLS = new Set([
  'read',
  'read-file',
  'write',
  'write-file',
  'edit',
]);

const looksLikeEndpointPath = (value: string): boolean =>
  value.startsWith('/') && !/[*+?()|[\]\\]/.test(value);

function endpointPathFromValue(value: string): string | undefined {
  if (looksLikeEndpointPath(value)) {
    return value;
  }

  try {
    const pathname = new URL(value).pathname;

    return looksLikeEndpointPath(pathname) ? pathname : undefined;
  } catch {
    return undefined;
  }
}

const looksLikeFilePath = (value: string): boolean =>
  !value.startsWith('/api') &&
  (value.includes('/') || /\.[a-z0-9]+$/i.test(value));

function firstString(
  record: Record<string, unknown>,
  keys: string[],
): string | undefined {
  for (const key of keys) {
    const value = record[key];

    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
}

export function openTargetFromTool({
  toolName,
  input,
}: {
  toolName: string;
  input: unknown;
}): SubagentOpenTarget | undefined {
  if (!input || typeof input !== 'object') {
    return undefined;
  }

  const record = input as Record<string, unknown>;
  const filePath = firstString(record, ['file_path', 'filePath', 'path']);
  const pattern = firstString(record, ['pattern', 'query', 'url', 'endpoint']);
  const normalized = toolName.replace(/_/g, '-').toLowerCase();

  if (filePath && (FILE_TOOLS.has(normalized) || looksLikeFilePath(filePath))) {
    return { kind: 'file', path: filePath };
  }

  const endpointPath = [pattern, filePath]
    .map((value) => (value ? endpointPathFromValue(value) : undefined))
    .find((value): value is string => typeof value === 'string');

  if (endpointPath) {
    return { kind: 'endpoint', path: endpointPath };
  }

  return undefined;
}
